import { create } from "zustand";
import { getBirthDayJuFromString } from "../util";

const FORTUNE_LAB_BASE_URL =
  process.env.REACT_APP_FORTUNE_LAB_BASE_URL || process.env.REACT_APP_BASE_URL || "";

export type SolarType = "SOLAR" | "LUNAR" | "LUNAR_INTER";

interface FortuneLabUserInput {
  name: string;
  phoneNumber: string;
  birthDate: string; // YYYY-MM-DD
  birthDayJu: string;
  solarType: SolarType;
  trkId: string;
}

interface FortuneLabRegisteredUser extends FortuneLabUserInput {
  id: string;
}

interface FortuneLabPostResponse {
  [key: string]: unknown;
}

interface FortuneLabState {
  userInput: FortuneLabUserInput | null;
  registeredUser: FortuneLabRegisteredUser | null;
  postData: FortuneLabPostResponse | null;
  registerLoading: boolean;
  error: string | null;
}

interface FortuneLabAction {
  registerUser: (input: {
    name: string;
    phoneNumber: string;
    birthDate: string;
    solarType: SolarType;
    trkId: string;
  }) => Promise<void>;
  trackLanding: (trkId: string) => Promise<void>;
  reset: () => void;
}

const initialState: FortuneLabState = {
  userInput: null,
  registeredUser: null,
  postData: null,
  registerLoading: false,
  error: null,
};

export const useFortuneLabStore = create<FortuneLabState & FortuneLabAction>((set) => ({
  ...initialState,
  registerUser: async ({ name, phoneNumber, birthDate, solarType, trkId }) => {
    const birthDayJu = getBirthDayJuFromString(birthDate, solarType);
    const payload = {
      name,
      phoneNumber,
      birthDate,
      birthDayJu,
      solarType,
      trkId,
    };

    set({ registerLoading: true, error: null, userInput: payload });

    try {
      const response = await fetch(
        `${FORTUNE_LAB_BASE_URL}/fortune-lab/user/post`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const postData: FortuneLabPostResponse = await response.json();

      let candidateId: unknown = null;
      if (postData && typeof postData === "object") {
        const contents = (postData as Record<string, unknown>).contents;
        if (contents && typeof contents === "object" && "id" in contents) {
          candidateId = (contents as Record<string, unknown>).id;
        } else if ("id" in postData) {
          candidateId = (postData as Record<string, unknown>).id;
        }
      }

      const sanitizedPhone = phoneNumber.replace(/[^0-9]/g, "");
      const fallbackId = sanitizedPhone ? sanitizedPhone : Date.now().toString();
      const id = String(candidateId ?? fallbackId);

      set({
        registeredUser: {
          ...payload,
          id,
        },
        postData,
        registerLoading: false,
      });
    } catch (error) {
      console.error("fortune-lab register error", error);
      set({ registerLoading: false, error: error instanceof Error ? error.message : "" });
    }
  },
  trackLanding: async (trkId: string) => {
    if (!trkId) {
      return;
    }

    try {
      await fetch(`${FORTUNE_LAB_BASE_URL}/fortune-lab/tracking/landing`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ trkId }),
      });
    } catch (error) {
      console.error("fortune-lab landing track error", error);
    }
  },
  reset: () => set(initialState),
}));
